import { singleton, inject } from "tsyringe";

import type {
    AiTutorSessionChallengeSourceDto,
    AiTutorSessionConceptEnhancementSourceDto,
    AiTutorSessionSourceDto,
    AiTutorSessionStudyReviewSourceDto,
    AiTutorSessionStudySourceDto,
} from "@/entities/ai-tutor-session/domain/dtos";
import { ConceptEnhancementChapterType } from "@/entities/concept-enhancement/domain/enums";
import type { AiTutorSessionBehaviorStructure } from "@/entities/ai-tutor-session/domain/behaviors/AiTutorSessionBehavior";

// pages
import type { SessionType } from "@/pages/ai-tutor/models/interface";

export interface BaseSessionNameStrategy<
    T extends AiTutorSessionSourceDto = AiTutorSessionSourceDto,
> {
    canHandle: (source: AiTutorSessionSourceDto) => source is T;
    build: (source: T) => {
        sessionType: SessionType;
        sessionName: string;
    };
}

/** studySession */
@singleton()
export class StudySessionNameStrategy
    implements BaseSessionNameStrategy<AiTutorSessionStudySourceDto>
{
    constructor(
        @inject("AiTutorSessionBehavior")
        private readonly aiTutorSessionBehavior: AiTutorSessionBehaviorStructure,
    ) {}

    public canHandle = (
        source: AiTutorSessionSourceDto,
    ): source is AiTutorSessionStudySourceDto => {
        return this.aiTutorSessionBehavior.isStudySource(source);
    };

    public build = (source: AiTutorSessionStudySourceDto) => {
        // 대단원 이름
        const bigUnitName = source.curriculum?.bigUnit.name ?? "···";

        // 학습 섹션 이름
        const studyItemTypeName = source.curriculum?.moduleName ?? "···";

        // 문항 번호
        const problemNumber = source.studyProblem.order;

        return {
            sessionType: "기본학습" as SessionType,
            sessionName: `${bigUnitName} ${studyItemTypeName} ${problemNumber}번`,
        };
    };
}

/**
 * studyReviewSession
 */
@singleton()
export class StudyReviewSessionNameStrategy
    implements BaseSessionNameStrategy<AiTutorSessionStudyReviewSourceDto>
{
    constructor(
        @inject("AiTutorSessionBehavior")
        private readonly aiTutorSessionBehavior: AiTutorSessionBehaviorStructure,
    ) {}

    public canHandle = (
        source: AiTutorSessionSourceDto,
    ): source is AiTutorSessionStudyReviewSourceDto => {
        return this.aiTutorSessionBehavior.isStudyReviewSource(source);
    };

    public build = (source: AiTutorSessionStudyReviewSourceDto) => {
        const problemNumber = source.studyReviewProblem.order;

        return {
            sessionType: "복습하기" as SessionType,
            sessionName: `복습 ${problemNumber}번`,
        };
    };
}

/**
 * challengeSession
 */
@singleton()
export class ChallengeSessionNameStrategy
    implements BaseSessionNameStrategy<AiTutorSessionChallengeSourceDto>
{
    constructor(
        @inject("AiTutorSessionBehavior")
        private readonly aiTutorSessionBehavior: AiTutorSessionBehaviorStructure,
    ) {}

    public canHandle = (
        source: AiTutorSessionSourceDto,
    ): source is AiTutorSessionChallengeSourceDto => {
        return this.aiTutorSessionBehavior.isChallengeSource(source);
    };

    public build = (source: AiTutorSessionChallengeSourceDto) => {
        const problemNumber = source.challengeProblem.order;

        const challengeTemplateName = source.challengeTemplate.title;

        return {
            sessionType: "챌린지업" as SessionType,
            sessionName: `${challengeTemplateName} ${problemNumber}번`,
        };
    };
}

/**
 * conceptEnhancementSession
 */
@singleton()
export class ConceptEnhancementSessionNameStrategy
    implements BaseSessionNameStrategy<AiTutorSessionConceptEnhancementSourceDto>
{
    constructor(
        @inject("AiTutorSessionBehavior")
        private readonly aiTutorSessionBehavior: AiTutorSessionBehaviorStructure,
    ) {}

    public canHandle = (
        source: AiTutorSessionSourceDto,
    ): source is AiTutorSessionConceptEnhancementSourceDto => {
        return this.aiTutorSessionBehavior.isConceptEnhancementSource(source);
    };

    public build = (source: AiTutorSessionConceptEnhancementSourceDto) => {
        const bigUnitName = source.curriculum?.bigUnit.name ?? "기타";

        const conceptEnhancementChapterType = {
            [ConceptEnhancementChapterType.Content]: "개념 점검하기",
            [ConceptEnhancementChapterType.Test]: "테스트 진행",
            [ConceptEnhancementChapterType.Pretest]: "학습 전 체크",
        };

        const chapterTypeName =
            conceptEnhancementChapterType[source.conceptEnhancementChapter.type];

        const problemNumber = source.conceptEnhancementProblem.order;

        return {
            sessionType: "튼튼개념쌓기" as SessionType,
            sessionName: `${bigUnitName} ${chapterTypeName} ${problemNumber}번`,
        };
    };
}
